import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/track";
import { LogoMark } from "@/components/Logo";

const TABS = [
  { key: "pipeline", label: "Pipeline" },
  { key: "funnel", label: "Funnel" },
  { key: "assessments", label: "Assessments" },
  { key: "quiz", label: "Leads" },
  { key: "golive", label: "Go-Live" },
];

const STATUS_STYLES = {
  quiz_lead: "text-sky-300 border-sky-400/50",
  lead: "text-slate-400 border-slate-600",
  assessment_purchased: "text-gold border-gold/50",
  intake_complete: "text-blue-400 border-blue-400/50",
  scheduled: "text-emerald-400 border-emerald-500/50",
  assessed: "text-emerald-300 border-emerald-400/70",
};

const prettyStatus = (s) => (s || "lead").replace(/_/g, " ");

export default function AdminPage() {
  const [auth, setAuth] = useState("checking");
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("pipeline");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [crm, setCrm] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [payMode, setPayMode] = useState(null);
  const [golive, setGolive] = useState(null);
  const [quizLeads, setQuizLeads] = useState(null);
  const [quizStats, setQuizStats] = useState(null);

  const loadAll = async (refreshDomain = false) => {
    const [c, a, s, p, g, q] = await Promise.all([
      axios.get(`${API_BASE}/admin/crm`, { withCredentials: true }),
      axios.get(`${API_BASE}/admin/analytics`, { withCredentials: true }),
      axios.get(`${API_BASE}/admin/assessments`, { withCredentials: true }),
      axios.get(`${API_BASE}/admin/payments-mode`, { withCredentials: true }).catch(() => null),
      axios.get(`${API_BASE}/admin/golive${refreshDomain ? "?refresh=1" : ""}`, { withCredentials: true }).catch(() => null),
      axios.get(`${API_BASE}/admin/quiz-results`, { withCredentials: true }).catch(() => null),
    ]);
    setCrm(c.data.contacts || []);
    setAnalytics(a.data);
    setAssessments(s.data.assessments || []);
    if (p) setPayMode(p.data);
    if (g) setGolive(g.data);
    if (q) {
      setQuizLeads(q.data.results || []);
      setQuizStats(q.data.stats || {});
    }
  };

  useEffect(() => {
    axios
      .get(`${API_BASE}/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        if (data.role === "admin") {
          setUser(data);
          setAuth("ok");
          loadAll().catch(() => {});
        } else {
          setAuth("denied");
        }
      })
      .catch(() => setAuth("login"));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API_BASE}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (data.role !== "admin") {
        setError("This account is not an admin.");
        setLoading(false);
        return;
      }
      setUser(data);
      setAuth("ok");
      await loadAll();
      setLoading(false);
    } catch (err) {
      setError(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Login failed");
      setLoading(false);
    }
  };

  const approve = async (id) => {
    try {
      await axios.post(`${API_BASE}/admin/assessments/${id}/approve`, {}, { withCredentials: true });
      await loadAll();
    } catch (err) {
      setError(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Approval failed");
    }
  };

  if (auth === "checking") {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gold" />
      </div>
    );
  }

  if (auth !== "ok") {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-5 py-20">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-gold/20 bg-obsidian-800/60 p-8"
        >
          <LogoMark className="w-12 h-12" />
          <h1 className="font-display text-2xl font-bold text-white mt-6">AIH Command Center</h1>
          <p className="text-slate-400 text-sm mt-2">Admin access — CRM, funnel and assessments.</p>
          <input
            data-testid="admin-login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            className="w-full mt-6 rounded-xl border border-slate-700 bg-obsidian-900/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-gold/60 focus:outline-none"
          />
          <input
            data-testid="admin-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full mt-3 rounded-xl border border-slate-700 bg-obsidian-900/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-gold/60 focus:outline-none"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-red-400 text-xs mt-3">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-5 w-full rounded-full bg-gold text-obsidian font-display font-bold py-3.5 hover:bg-gold-bright transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
          </button>
        </form>
      </div>
    );
  }

  const funnelSteps = ["view_offer", "click_cta", "begin_checkout", "purchase", "intake_submitted", "book_call"];
  const maxCount = analytics ? Math.max(...funnelSteps.map((s) => analytics.counts[s] || 0), 1) : 1;

  return (
    <div className="min-h-screen bg-obsidian px-5 py-12 sm:py-16 grain" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <LogoMark className="w-11 h-11" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">AIH Command Center</p>
              <h1 className="font-display text-xl sm:text-2xl font-bold text-white">Revenue Pipeline</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {payMode && (
              <span
                data-testid="admin-payments-mode"
                className={`rounded-full border px-4 py-2 text-[11px] font-mono uppercase tracking-[0.15em] ${
                  payMode.mode === "live"
                    ? "border-emerald-500/60 text-emerald-400 bg-emerald-500/10"
                    : "border-gold/50 text-gold bg-gold/10"
                }`}
              >
                Stripe: {payMode.mode === "live" ? "Live" : "Test mode"}
              </span>
            )}
            <button
            data-testid="admin-logout"
            onClick={async () => {
              await axios.post(`${API_BASE}/auth/logout`, {}, { withCredentials: true });
              setAuth("login");
              setUser(null);
            }}
            className="rounded-full border border-slate-600 text-slate-300 text-sm px-5 py-2 hover:border-gold hover:text-gold transition-colors"
          >
            Sign Out
          </button>
          </div>
        </div>

        <div className="flex gap-2 mt-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-5 py-2.5 text-sm font-display font-bold transition-colors ${
                tab === t.key ? "bg-gold text-obsidian" : "border border-slate-700 text-slate-300 hover:border-gold/50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "golive" && golive && (
          <div className="mt-8 space-y-5" data-testid="admin-golive">
            <div className="rounded-2xl border border-slate-800 bg-obsidian-800/50 p-6" data-testid="golive-stripe">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-display text-lg font-bold text-white">1 · Stripe Payments</h3>
                <span className={`rounded-full border px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] ${golive.stripe_mode === "live" ? "border-emerald-500/60 text-emerald-400 bg-emerald-500/10" : "border-gold/50 text-gold bg-gold/10"}`}>
                  {golive.stripe_mode === "live" ? "Live" : "Test mode"}
                </span>
              </div>
              {golive.stripe_mode === "live" ? (
                <p className="text-slate-300 text-sm mt-4 leading-relaxed">
                  Live payments active. Run one real $7 product purchase to smoke-test, and confirm
                  the payout in your Stripe dashboard.
                </p>
              ) : (
                <ol className="mt-4 space-y-2.5 text-sm text-slate-300 leading-relaxed list-decimal list-inside">
                  <li>Click <span className="text-gold">Manage</span> (next to Preview) → <span className="text-gold">Payments</span> tab.</li>
                  <li>Click <span className="text-gold">"Claim your Stripe sandbox"</span> and finish Stripe registration (identity/payout verification if asked).</li>
                  <li>Install the Emergent app when you land on the Stripe dashboard.</li>
                  <li>Press <span className="text-gold">Deploy</span> — live keys, webhooks and catalog attach automatically.</li>
                </ol>
              )}
            </div>

            <div className="rounded-2xl border border-slate-800 bg-obsidian-800/50 p-6" data-testid="golive-domain">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-display text-lg font-bold text-white">2 · Custom Domain — allinhandmarketing.com</h3>
                <span className={`rounded-full border px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] ${golive.domain.connected ? "border-emerald-500/60 text-emerald-400 bg-emerald-500/10" : "border-gold/50 text-gold bg-gold/10"}`}>
                  {golive.domain.connected ? "Connected" : "Not connected"}
                </span>
              </div>
              <p className="text-slate-400 text-sm mt-3">{golive.domain.detail}</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-700/60 bg-obsidian-900/50 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-2">Option A · Auto-link (recommended)</p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Manage Publishes → Domain tab → enter allinhandmarketing.com → Auto-link Domain →
                    verify with the code sent to your registrar email → 2-5 minutes.
                  </p>
                </div>
                <div className="rounded-xl border border-slate-700/60 bg-obsidian-900/50 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">Option B · Manual DNS</p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    At Unstoppable Domains add: A @ 162.159.142.117 · A @ 172.66.2.113 · CNAME www
                    allinhandmarketing.com — remove other A records → back here, Check Status → 5-15 min.
                  </p>
                </div>
              </div>
              <p className="text-slate-600 text-xs mt-4 leading-relaxed">
                SSL is automatic (Cloudflare custom hostnames). Your /go path is already routed by the
                app. If the domain is a blockchain (.crypto-style) domain it may not support standard
                DNS records — check your Unstoppable Domains dashboard.
              </p>
              <button
                data-testid="golive-domain-refresh"
                onClick={() => loadAll(true)}
                className="mt-4 rounded-full border border-slate-600 text-slate-300 text-xs px-5 py-2 hover:border-gold hover:text-gold transition-colors"
              >
                Re-check Domain Now
              </button>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-obsidian-800/50 p-6" data-testid="golive-catalog">
              <h3 className="font-display text-lg font-bold text-white">3 · Product Catalog</h3>
              <p className="text-slate-300 text-sm mt-3">
                {golive.products_count} products seeded (2 assessments + {Math.max(golive.products_count - 2, 0)} store items).{" "}
                {golive.stripe_mode === "live"
                  ? "The live catalog rebuilt automatically at startup."
                  : "Going live recreates them in your live Stripe account automatically — nothing to redo."}
              </p>
            </div>
          </div>
        )}

        {error && <p className="text-red-400 text-xs mt-4">{error}</p>}

        {tab === "pipeline" && (
          <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-800" data-testid="admin-crm-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-slate-500 font-mono text-[10px] uppercase tracking-[0.2em] border-b border-slate-800 bg-obsidian-800/60">
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Source</th>
                  <th className="px-5 py-4">Offer</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Value</th>
                </tr>
              </thead>
              <tbody>
                {crm.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-slate-500">
                      No CRM records yet — they appear automatically as leads arrive.
                    </td>
                  </tr>
                )}
                {crm.map((c) => (
                  <tr key={c.email} className="border-b border-slate-800/60 hover:bg-obsidian-800/40">
                    <td className="px-5 py-4">
                      <p className="text-white font-semibold">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</p>
                      <p className="text-slate-500 text-xs">{c.email}{c.business_name ? ` · ${c.business_name}` : ""}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-300">{c.source || "direct"}</td>
                    <td className="px-5 py-4 text-slate-300">{c.offer || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-block rounded-full border px-3 py-1 text-[11px] font-mono ${STATUS_STYLES[c.status] || STATUS_STYLES.lead}`}>
                        {prettyStatus(c.status)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gold font-display font-bold">${(c.value || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "funnel" && analytics && (
          <div className="mt-8" data-testid="admin-funnel">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-gold/25 bg-obsidian-800/60 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Revenue collected</p>
                <p className="font-display text-3xl font-extrabold text-gold mt-2">${analytics.revenue.toFixed(2)}</p>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-obsidian-800/60 p-6 sm:col-span-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-3">Checkouts by source</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(analytics.checkouts_by_source).length === 0 && (
                    <span className="text-slate-500 text-sm">No checkout attempts yet.</span>
                  )}
                  {Object.entries(analytics.checkouts_by_source).map(([s, n]) => (
                    <span key={s} className="rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300">
                      {s}: {n}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-6 rounded-2xl border border-slate-800 bg-obsidian-800/50 p-6 space-y-4">
              {funnelSteps.map((s) => (
                <div key={s} data-testid={`funnel-step-${s}`}>
                  <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                    <span className="font-mono uppercase tracking-[0.15em]">{s.replace(/_/g, " ")}</span>
                    <span>{analytics.counts[s] || 0}</span>
                  </div>
                  <div className="h-2 rounded-full bg-obsidian-900 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-gold to-emerald-500 transition-all duration-700"
                      style={{ width: `${Math.max(((analytics.counts[s] || 0) / maxCount) * 100, (analytics.counts[s] || 0) > 0 ? 4 : 0)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "assessments" && (
          <div className="mt-8 space-y-5" data-testid="admin-assessments">
            {assessments.length === 0 && (
              <div className="rounded-2xl border border-slate-800 bg-obsidian-800/50 p-10 text-center text-slate-500">
                No intake records yet.
              </div>
            )}
            {assessments.map((a) => (
              <div key={a.id} className="rounded-2xl border border-slate-800 bg-obsidian-800/50 p-6" data-testid="admin-assessment-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-white font-semibold">
                      {[a.first_name, a.last_name].filter(Boolean).join(" ")}
                      {a.business_name ? ` — ${a.business_name}` : ""}
                    </p>
                    <p className="text-slate-500 text-xs mt-1">
                      {a.email} · {a.offer_type === "express" ? "Business Snapshot" : "Growth Assessment"} · status: {prettyStatus(a.status)}
                    </p>
                  </div>
                  {a.offer_type !== "express" && a.status === "draft_ready" && !a.report && (
                    <button
                      data-testid={`admin-approve-${a.id}`}
                      onClick={() => approve(a.id)}
                      className="rounded-full bg-gold text-obsidian font-display font-bold px-5 py-2.5 text-sm hover:bg-gold-bright transition-colors"
                    >
                      Review &amp; Approve → Publish
                    </button>
                  )}
                  {a.report && (
                    <span className="rounded-full border border-emerald-500/50 text-emerald-400 text-[11px] font-mono px-3 py-1.5">
                      Published ✓
                    </span>
                  )}
                </div>

                {(a.draft || []).length > 0 && (
                  <div className="mt-5 border-t border-slate-800 pt-5 space-y-4">
                    {(a.report || a.draft).slice(0, 3).map((s, i) => (
                      <div key={i}>
                        <p className="text-gold text-xs font-semibold">{s.title}</p>
                        <p className="text-slate-400 text-xs leading-relaxed mt-1 line-clamp-2">{s.body}</p>
                      </div>
                    ))}
                    <p className="text-slate-600 text-[10px]">
                      Showing 3 of {(a.report || a.draft).length} sections — the customer sees the full report in their portal.
                    </p>
                  </div>
                )}
                {!a.draft && (
                  <p className="mt-4 text-slate-500 text-xs flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> AI engine drafting findings…
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "quiz" && (
          <div className="mt-8 space-y-5" data-testid="admin-quiz-leads">
            {(!quizLeads || !quizStats) ? (
              <div className="rounded-2xl border border-slate-800 bg-obsidian-800/50 p-10 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gold" />
              </div>
            ) : (
            <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                ["quiz_started", "Quiz starts"],
                ["quiz_completed", "Results calculated"],
                ["quiz_result_viewed", "Result views"],
                ["quiz_offer_clicked", "Offer clicks"],
              ].map(([k, label]) => (
                <div key={k} className="rounded-2xl border border-slate-800 bg-obsidian-800/60 p-5" data-testid={`quiz-stat-${k}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</p>
                  <p className="font-display text-2xl font-extrabold text-gold mt-2">{quizStats[k] || 0}</p>
                </div>
              ))}
            </div>
            {quizLeads.length === 0 && (
              <div className="rounded-2xl border border-slate-800 bg-obsidian-800/50 p-10 text-center text-slate-500">
                No quiz leads yet — results appear here as visitors complete the Foundations Check.
              </div>
            )}
            {quizLeads.length > 0 && (
              <div className="overflow-x-auto rounded-2xl border border-slate-800">
                <table className="w-full text-sm min-w-[900px]">
                  <thead>
                    <tr className="text-left text-slate-500 font-mono text-[10px] uppercase tracking-[0.2em] border-b border-slate-800 bg-obsidian-800/60">
                      <th className="px-5 py-4">Lead</th>
                      <th className="px-5 py-4">Track</th>
                      <th className="px-5 py-4">Score</th>
                      <th className="px-5 py-4">Priority gap</th>
                      <th className="px-5 py-4">Next offer</th>
                      <th className="px-5 py-4">Promo OK</th>
                      <th className="px-5 py-4">Source</th>
                      <th className="px-5 py-4">Follow-up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quizLeads.map((r) => (
                      <tr key={r.id} className="border-b border-slate-800/60 hover:bg-obsidian-800/40">
                        <td className="px-5 py-4">
                          <p className="text-white font-semibold">{r.first_name || "—"}</p>
                          <p className="text-slate-500 text-xs">{r.email}</p>
                        </td>
                        <td className="px-5 py-4 text-slate-300">{r.track === "planning" ? "Planning" : "Operating"}</td>
                        <td className="px-5 py-4">
                          <span className="text-gold font-display font-bold">{r.score}/100</span>
                          <p className="text-slate-500 text-xs">{r.band_label}</p>
                        </td>
                        <td className="px-5 py-4 text-slate-300 max-w-[220px]">{r.priority_gap?.title || "—"}</td>
                        <td className="px-5 py-4 text-slate-300">{r.next_offer?.name || "—"}</td>
                        <td className="px-5 py-4">
                          {r.promo_consent ? (
                            <span className="text-emerald-400">Yes</span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-slate-300">{r.source || "direct"}</td>
                        <td className="px-5 py-4">
                          <select
                            data-testid={`quiz-followup-${r.id}`}
                            value={r.follow_up_status || "new"}
                            onChange={async (e) => {
                              try {
                                await axios.post(
                                  `${API_BASE}/admin/quiz-results/${r.id}/status`,
                                  { status: e.target.value },
                                  { withCredentials: true }
                                );
                                setQuizLeads((prev) =>
                                  prev.map((x) => (x.id === r.id ? { ...x, follow_up_status: e.target.value } : x))
                                );
                              } catch {
                                /* keep previous state on failure */
                              }
                            }}
                            className="rounded-lg border border-slate-700 bg-obsidian-900/70 px-3 py-1.5 text-xs text-slate-200 focus:border-gold/60 focus:outline-none"
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="won">Won</option>
                            <option value="archived">Archived</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
